<template>
  <RevCard class="p-24 md:p-32">
    <h1 class="heading-2 mb-32">
      {{ i18n(translations.title) }}
    </h1>
    <div class="grid gap-24 md:grid-cols-2 md:gap-32">
      <RevCard
        v-for="method in paymentMethods"
        :key="`payment-method-${method.code}`"
        class="flex flex-col gap-16 p-24"
        :data-test="`payment-method-${method.code}`"
      >
        <div class="flex items-center">
          <div class="flex flex-wrap gap-12">
            <h2 class="text-static-default-hi heading-3 md:heading-2">
              {{ getMethodTitle(method.code) }}
            </h2>

            <RevTag
              v-if="method.preferred"
              class="self-center"
              :label="i18n(translations.payment_method_preferred_label)"
              size="large"
              variant="primary"
            />
          </div>

          <IconCheckInCircle
            v-if="method.configured"
            class="text-static-success-hi ml-auto mt-4 shrink-0 self-start"
            :data-test="`payment-method-${method.code}-check-icon`"
          />
          <IconCrossInCircle
            v-else
            class="text-static-danger-hi ml-auto mt-4 shrink-0 self-start"
            :data-test="`payment-method-${method.code}-cross-icon`"
          />
        </div>

        <p class="text-static-default-low">
          {{ getMethodDescription(method.code) }}
        </p>

        <div v-if="method.code === PaymentMethodCode.card" class="mb-8">
          <p class="text-static-default-low body-2-bold mb-8">
            {{ i18n(translations.payment_method_cards_accepted) }}
          </p>
          <PaymentIcons :methods="tradeInPaymentMethods"></PaymentIcons>
        </div>

        <div class="mt-auto flex gap-12">
          <RevButton
            size="small"
            :to="{
              name: ROUTE_NAMES.BUYBACK.OPTIONS.UPDATE_PAYMENT_METHOD,
              params: { code: method.code },
            }"
            variant="secondary"
          >
            {{ i18n(translations.payment_method_edit_cta) }}
          </RevButton>
          <RevButton
            v-if="!method.preferred"
            :data-test="`payment-method-${method.code}-preferred-cta`"
            size="small"
            variant="secondary"
            @click="updateBankTransferPreference(method.code)"
          >
            {{ i18n(translations.payment_method_preferred_cta) }}
          </RevButton>
        </div>
      </RevCard>
    </div>
  </RevCard>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import type { HttpApiError } from '@backmarket/http-api'
import {
  getRefurbisherPaymentMethodsConfig,
  putBankTransferPreference,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher'
import {
  type GetRefurbisherPaymentMethodsConfig,
  PaymentMethodCode,
  type PutBankTransferPreference,
} from '@backmarket/http-api/src/api-specs-buyback/seller/refurbisher.types'
import {
  PaymentIcons,
  useMarketPaymentMethodsFor,
} from '@backmarket/nuxt-layer-payment/methods'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevTag } from '@ds/components/Tag'
import { IconCheckInCircle } from '@ds/icons/IconCheckInCircle'
import { IconCrossInCircle } from '@ds/icons/IconCrossInCircle'

import { ROUTE_NAMES } from '~/constants/routes'

import translations from './Payment.translations'

interface PaymentMethodSection {
  code: PaymentMethodCode
  configured: boolean
  preferred?: boolean
}

const i18n = useI18n()
const logger = useLogger()
const { openErrorToast, openSuccessToast } = useTheToast()

const isLoading = ref(false)
const paymentMethods = ref<PaymentMethodSection[]>()

const { error: paymentMethodsConfigError, data: paymentMethodsConfig } =
  await useHttpFetch(getRefurbisherPaymentMethodsConfig)

function updatePaymentMethods(
  config: GetRefurbisherPaymentMethodsConfig,
): PaymentMethodSection[] | undefined {
  const { currenciesPaymentMethods } = config

  // Multiple payment methods configs are allowed by the backend
  // But for the moment, a refurbisher is configured to only use one
  const currencyPaymentMethod = currenciesPaymentMethods[0]

  return currencyPaymentMethod?.paymentMethods?.map((method) => {
    const code = method.code.toLowerCase() as PaymentMethodCode

    const preferred =
      code === PaymentMethodCode.card
        ? !currencyPaymentMethod.bankTransferPreferred
        : currencyPaymentMethod.bankTransferPreferred

    return {
      code,
      configured: method.configured,
      preferred,
    }
  })
}

if (paymentMethodsConfig.value) {
  paymentMethods.value = updatePaymentMethods(paymentMethodsConfig.value)
}

if (paymentMethodsConfigError.value) {
  logger.error('[BUYBACK][API][PAYMENT_METHOD] Error getting payment methods', {
    error: paymentMethodsConfigError.value,
    owners: ['bot-squad-circularity-order-lifecycle-front'],
  })
}

const { data: tradeInPaymentMethods } = await useMarketPaymentMethodsFor({
  tenant: 'TRADEIN',
})

function getMethodTitle(methodCode: PaymentMethodCode): string {
  const translationKey =
    methodCode === PaymentMethodCode.card
      ? 'payment_method_card_title'
      : 'payment_method_bank_transfer_title'

  return i18n(translations[translationKey])
}

function getMethodDescription(methodCode: PaymentMethodCode): string {
  const translationKey =
    methodCode === PaymentMethodCode.card
      ? 'payment_method_card_description'
      : 'payment_method_bank_transfer_description'

  return i18n(translations[translationKey])
}

async function updateBankTransferPreference(
  preferredMethodCode: PaymentMethodCode,
) {
  try {
    isLoading.value = true

    const bankTransferMethod = paymentMethods.value?.find(
      (method) => method.code !== PaymentMethodCode.card,
    )

    if (!bankTransferMethod) {
      return
    }

    const paymentMethodCode = bankTransferMethod.code.toUpperCase()
    const bankTransferPreferred = preferredMethodCode !== PaymentMethodCode.card

    await $httpFetch(putBankTransferPreference, {
      body: {
        paymentMethodCode,
        bankTransferPreferred,
      } as PutBankTransferPreference,
    })

    paymentMethods.value?.forEach((method, index) => {
      if (!paymentMethods.value) {
        return
      }
      if (method.code === PaymentMethodCode.card) {
        paymentMethods.value[index].preferred = !bankTransferPreferred
      } else {
        paymentMethods.value[index].preferred = bankTransferPreferred
      }
    })

    openSuccessToast()
  } catch (err) {
    logger.error(
      '[BUYBACK][API][PAYMENT_METHOD] Error updating bank transfer preference',
      {
        error: err as HttpApiError,
        owners: ['bot-squad-circularity-order-lifecycle-front'],
      },
    )

    openErrorToast()
  } finally {
    isLoading.value = false
  }
}
</script>
